/**
 * OCRInvoiceReview - Revue et édition des données OCR avant création de facture
 * Design System: Dashboard context (bg-white, text-gray-900)
 */

import { useState, useCallback } from 'react';
import {
  FileText,
  User,
  Calendar,
  Hash,
  Plus,
  Trash2,
  CheckCircle,
  ArrowLeft,
  Loader2,
  Package,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { showSuccess, showError } from '@/lib/notifications';
import { importService } from '../services/import.service';

interface OCRExtractedData {
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  supplier?: {
    name?: string | null;
    siret?: string | null;
    tvaNumber?: string | null;
    address?: string | null;
  };
  customer?: {
    name?: string | null;
    siret?: string | null;
    tvaNumber?: string | null;
    address?: string | null;
  };
  amounts?: {
    subtotal?: number | null;
    tax?: number | null;
    total?: number | null;
  };
  lineItems?: Array<{
    quantity?: number;
    description?: string;
    unitPrice?: number;
    totalHT?: number;
    vatRate?: number;
  }>;
  currency?: string;
  confidence: number;
}

interface OCRInvoiceReviewProps {
  extractedData: OCRExtractedData;
  provider: string;
  processingTime?: number;
  onBack: () => void;
  onInvoiceCreated: (invoiceId: string, invoiceNumber: string) => void;
}

interface EditableItem {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

export function OCRInvoiceReview({
  extractedData,
  provider,
  processingTime,
  onBack,
  onInvoiceCreated,
}: OCRInvoiceReviewProps) {
  // Form state initialized from OCR data
  const [invoiceNumber, setInvoiceNumber] = useState(extractedData.invoiceNumber || '');
  const [invoiceDate, setInvoiceDate] = useState(
    extractedData.invoiceDate
      ? new Date(extractedData.invoiceDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  );
  const [dueDate, setDueDate] = useState(
    extractedData.dueDate
      ? new Date(extractedData.dueDate).toISOString().split('T')[0]
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );

  // Client info — priorité au customer (destinataire), fallback sur supplier si customer absent
  const clientSource = extractedData.customer?.name ? extractedData.customer : extractedData.supplier;
  const [clientName, setClientName] = useState(clientSource?.name || '');
  const [clientSiret, setClientSiret] = useState(clientSource?.siret || '');
  const [clientVat, setClientVat] = useState(clientSource?.tvaNumber || '');
  const [clientAddress, setClientAddress] = useState(clientSource?.address || '');

  // Estimer le taux de TVA depuis les montants OCR si disponibles
  const estimatedVatRate = (() => {
    const subtotal = extractedData.amounts?.subtotal;
    const tax = extractedData.amounts?.tax;
    if (subtotal && tax && subtotal > 0) {
      return Math.round((tax / subtotal) * 100 * 10) / 10; // Arrondi à 0.1%
    }
    return 0; // Pas de taux devinable — l'utilisateur devra le renseigner
  })();

  // Line items
  const [items, setItems] = useState<EditableItem[]>(() => {
    if (extractedData.lineItems && extractedData.lineItems.length > 0) {
      return extractedData.lineItems.map((item) => ({
        description: item.description || '',
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || 0,
        vatRate: item.vatRate ?? estimatedVatRate,
      }));
    }
    // Fallback: create a single line from totals
    return [
      {
        description: 'Prestation importée',
        quantity: 1,
        unitPrice: extractedData.amounts?.subtotal || extractedData.amounts?.total || 0,
        vatRate: estimatedVatRate,
      },
    ];
  });

  // Title
  const [title, setTitle] = useState('');

  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();

  // Computed totals
  const totalHT = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const totalTVA = items.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice * (item.vatRate / 100),
    0
  );
  const totalTTC = totalHT + totalTVA;

  const confidencePercent = Math.round(extractedData.confidence * 100);
  const confidenceColor =
    confidencePercent >= 80
      ? 'text-green-600 bg-green-50 border-green-200'
      : confidencePercent >= 60
      ? 'text-amber-600 bg-amber-50 border-amber-200'
      : 'text-red-600 bg-red-50 border-red-200';

  const handleAddItem = useCallback(() => {
    setItems((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0, vatRate: estimatedVatRate }]);
  }, []);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleItemChange = useCallback(
    (index: number, field: keyof EditableItem, value: string | number) => {
      setItems((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, [field]: field === 'description' ? value : Number(value) }
            : item
        )
      );
    },
    []
  );

  const handleCreateInvoice = useCallback(async () => {
    // Validation
    if (!clientName.trim()) {
      showError('Le nom du client est requis', 'Validation');
      return;
    }
    if (!invoiceDate) {
      showError('La date de facture est requise', 'Validation');
      return;
    }
    if (!dueDate) {
      showError('La date d\'échéance est requise', 'Validation');
      return;
    }
    if (items.length === 0) {
      showError('Au moins une ligne est requise', 'Validation');
      return;
    }
    if (items.some((item) => !item.description.trim())) {
      showError('Toutes les lignes doivent avoir une description', 'Validation');
      return;
    }

    setIsCreating(true);

    try {
      const result = await importService.createInvoiceFromOCR({
        invoiceNumber: invoiceNumber || undefined,
        invoiceDate,
        dueDate,
        client: {
          companyName: clientName,
          vatNumber: clientVat || undefined,
          siret: clientSiret || undefined,
          address: clientAddress || undefined,
        },
        items: items.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          vatRate: item.vatRate,
        })),
        title: title.trim() || undefined,
        internalNotes: `Importé via OCR (${provider})\nConfiance: ${confidencePercent}%\nN° original: ${invoiceNumber || 'N/A'}`,
      });

      // Invalider les caches liés aux factures pour éviter les données stale
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', 'statistics', 'invoice'] });

      showSuccess(
        `Facture ${result.data.invoice.number} créée en brouillon (${result.data.invoice.totalTTC}€ TTC)`,
        'Facture importée'
      );
      onInvoiceCreated(result.data.invoice.id, result.data.invoice.number);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la création';
      showError(message, 'Échec');
    } finally {
      setIsCreating(false);
    }
  }, [
    clientName,
    clientSiret,
    clientVat,
    clientAddress,
    invoiceNumber,
    invoiceDate,
    dueDate,
    items,
    title,
    provider,
    confidencePercent,
    onInvoiceCreated,
    queryClient,
  ]);

  return (
    <div className="space-y-6">
      {/* Header with confidence badge */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${confidenceColor}`}>
            Confiance OCR: {confidencePercent}%
          </span>
          {processingTime && (
            <span className="text-xs text-gray-500">
              {(processingTime / 1000).toFixed(1)}s ({provider})
            </span>
          )}
        </div>
      </div>

      {/* OCR Review Form */}
      <div className="bg-white/40 backdrop-blur-sm rounded-2xl border border-gray-200/50 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-600" />
          Vérifier les données extraites
        </h3>

        {/* Invoice Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Hash className="w-3.5 h-3.5 inline mr-1" />
              N° Facture (original)
            </label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Ex: FA-2024-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
            <p className="text-xs text-gray-500 mt-1">Un nouveau n° sera attribué automatiquement</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              Date de facture *
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              Date d'échéance *
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Invoice Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <FileText className="w-3.5 h-3.5 inline mr-1" />
            Titre de la facture
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Travaux de rénovation salle de bain"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
          <p className="text-xs text-gray-500 mt-1">Apparaîtra sur le PDF de la facture (optionnel)</p>
        </div>

        {/* Client Info */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-teal-600" />
            Client
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom / Raison sociale *</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Nom du client"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N° TVA</label>
              <input
                type="text"
                value={clientVat}
                onChange={(e) => setClientVat(e.target.value)}
                placeholder="FR12345678901"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SIRET</label>
              <input
                type="text"
                value={clientSiret}
                onChange={(e) => setClientSiret(e.target.value)}
                placeholder="12345678901234"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <input
                type="text"
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                placeholder="Adresse du client"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-teal-600" />
              Lignes de facture
            </h4>
            <button
              onClick={handleAddItem}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Ajouter
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white/40">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 text-gray-700">
                  <th className="text-left px-4 py-3 font-medium">Description</th>
                  <th className="text-right px-4 py-3 font-medium w-24">Qté</th>
                  <th className="text-right px-4 py-3 font-medium w-32">Prix unit. (€)</th>
                  <th className="text-right px-4 py-3 font-medium w-24">TVA (%)</th>
                  <th className="text-right px-4 py-3 font-medium w-32">Total HT (€)</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Description de la ligne"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-gray-900 focus:ring-1 focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="0.01"
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-right text-gray-900 focus:ring-1 focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-right text-gray-900 focus:ring-1 focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={item.vatRate}
                        onChange={(e) => handleItemChange(index, 'vatRate', e.target.value)}
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-full px-2 py-1 border border-gray-200 rounded text-right text-gray-900 focus:ring-1 focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">
                      {(item.quantity * item.unitPrice).toFixed(2)}
                    </td>
                    <td className="px-2 py-2">
                      {items.length > 1 && (
                        <button
                          onClick={() => handleRemoveItem(index)}
                          className="p-1 text-red-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="bg-gray-50/80 rounded-xl p-4 space-y-2 min-w-[250px]">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Total HT</span>
              <span className="font-medium text-gray-900">{totalHT.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>TVA</span>
              <span className="font-medium text-gray-900">{totalTVA.toFixed(2)} €</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between text-base font-semibold text-gray-900">
              <span>Total TTC</span>
              <span className="text-teal-600">{totalTTC.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            La facture sera créée en <span className="font-medium text-amber-600">brouillon</span>.
            Vous pourrez la modifier avant de l'envoyer.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              disabled={isCreating}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleCreateInvoice}
              disabled={isCreating || !clientName.trim() || items.length === 0}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Créer la facture
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
